// Library methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import NumberInputField from "src/components/shared/inputs/NumberInputField/NumberInputField";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Types
import { ExamLensType } from "src/services/exams/types";

// Utils
import {
  IOL_ENUM_FIELDS,
  IOL_TORIC_CYLINDER,
} from "src/utils/exams/lensSimulationHelper";

// The fields deviceUpdateLensParameter's `data` accepts, minus lensName
// (the parent fills that in) -- derived from the function itself so this
// stays in sync with api.ts.
type LensParameterUpdate = Partial<
  Omit<Parameters<typeof deviceUpdateLensParameter>[2], "lensName">
>;

type IOLOptionsType = {
  currentLens: ExamLensType | undefined;
  isCurrentLensActionDisabled: boolean;
  sendLensParameterUpdate: (update: LensParameterUpdate) => Promise<void>;
  eye?: string; // null or "Both" = applies to both eyes. Else, "Left" or "Right"
};

const IOLOptions = ({
  currentLens,
  isCurrentLensActionDisabled,
  sendLensParameterUpdate,
  eye,
}: IOLOptionsType) => {
  const { t } = useTranslation();

  const [toricCylinderValue, setToricCylinderValue] = useState(() =>
    typeof currentLens?.toricCylinderType === "number"
      ? String(currentLens.toricCylinderType)
      : ""
  );

  // Show the toric cylinder input if the value exists and isn't 0
  const [isToric, setIsToric] = useState(
    () =>
      typeof currentLens?.toricCylinderType === "number" &&
      currentLens?.toricCylinderType !== 0
  );

  const handleToggleToric = () => {
    const toggleVal = !isToric;
    setIsToric(toggleVal);

    const cylinder = toggleVal ? IOL_TORIC_CYLINDER.min : null;
    setToricCylinderValue(cylinder === null ? "" : String(cylinder));
    sendLensParameterUpdate({
      toricCylinderType: cylinder,
      ...(eye && { eye }),
    });
  };

  // Defaults to each field's first option -- matches AddEditIOLModal's
  // default when adding a new lens preset -- unless currentLens already has
  // a valid value for it.
  const [iolEnumValues, setIolEnumValues] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        IOL_ENUM_FIELDS.map((field) => {
          const currentValue = currentLens?.[field.parameterName];
          const isValid = (field.options as readonly string[]).includes(
            currentValue ?? ""
          );
          return [
            field.parameterName,
            isValid ? (currentValue as string) : field.options[0],
          ];
        })
      )
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: eye
          ? "1fr"
          : {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
            },
        gap: "8px",
      }}
    >
      {IOL_ENUM_FIELDS.map(({ parameterName, title, tooltip, options }) => (
        <Box
          key={parameterName}
          display="flex"
          flexDirection="column"
          gap="4px"
        >
          <TitleWithTooltip
            title={t(title)}
            tooltip={<div>{t(tooltip)}</div>}
            titleStyle={{ fontSize: "12px" }}
            tooltipStyle={{ width: "16px", height: "16px" }}
          />

          <Box display="flex" gap="4px">
            {options.map((option) => (
              <ExamCardButton
                key={`${parameterName}-${option}`}
                label={t(option)}
                isSelected={iolEnumValues[parameterName] === option}
                isDisabled={isCurrentLensActionDisabled}
                updateValue={() => {
                  setIolEnumValues((prev) => ({
                    ...prev,
                    [parameterName]: option,
                  }));
                  sendLensParameterUpdate({
                    [parameterName]: option,
                    ...(eye && { eye }),
                  } as LensParameterUpdate);
                }}
              />
            ))}
          </Box>
        </Box>
      ))}

      <Box
        sx={{ gridColumn: "1 / -1" }}
        display="flex"
        alignItems="center"
        gap="8px"
      >
        <CustomCheckbox
          id={`live-iol-toric-checkbox${eye ? `-${eye.toLowerCase()}` : ""}`}
          checked={isToric}
          onClick={handleToggleToric}
          disabled={isCurrentLensActionDisabled}
          checkedFill="#E2772E"
          checkedStroke="#FFF"
          uncheckedFill="#FFF"
          uncheckedStroke="#5B6D7A"
        />
        <Typography sx={{ fontSize: "12px" }}>{t("Toric")}</Typography>
      </Box>

      {isToric && (
        <Box display="flex" flexDirection="column" gap="4px">
          <TitleWithTooltip
            title={t(IOL_TORIC_CYLINDER.title)}
            tooltip={<div>{t(IOL_TORIC_CYLINDER.tooltip)}</div>}
            titleStyle={{ fontSize: "12px" }}
            tooltipStyle={{ width: "16px", height: "16px" }}
          />

          <NumberInputField
            id={`live-iol-toric-cylinder-input${eye ? eye?.toLowerCase() : ""}`}
            disabled={isCurrentLensActionDisabled}
            placeholder={t("Enter a value")}
            value={toricCylinderValue}
            min={IOL_TORIC_CYLINDER.min}
            max={IOL_TORIC_CYLINDER.max}
            onChange={(e) => setToricCylinderValue(e.target.value ?? "")}
            onBlur={(finalValue) =>
              sendLensParameterUpdate({
                toricCylinderType:
                  finalValue === "" ? null : Number(finalValue),
                ...(eye && { eye }),
              })
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default IOLOptions;
